import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { FadeLink, ExternalLink, Section, cx, useCopy } from './core';
import logoMark from './assets/img/logo-mark.png';

export const CONTACT = {
  phone: '+852 5105 8215',
  phoneHref: 'tel:+85251058215',
  email: 'info@agentecfusion.com',
  research: 'jinhuqi@agentecfusion.com',
  business: 'qiudanwang@agentecfusion.com',
  linkedin: 'https://www.linkedin.com/company/agentecfusion/'
};

const chrome = {
  en: {
    home: 'AgentecFusion home', primary: 'Primary', mobile: 'Mobile', language: 'Language', contact: 'Contact', skip: 'Skip to content',
    openMenu: 'Open menu', closeMenu: 'Close menu',
    nav: [['/products', 'Products'], ['/solutions', 'Solutions'], ['/partnerships', 'Partnerships'], ['/about', 'About']],
    tagline: 'AI products for everyday work.', tel: 'Tel', email: 'Email',
    products: 'Products', company: 'Company', connect: 'Connect', account: 'Account center', admin: 'Administration',
    privacy: 'Privacy', terms: 'Terms', rights: 'All rights reserved.', motto: 'Research · Products · Collaboration',
    nextTitle: 'Let’s build what’s next.', contactTeam: 'Contact the team', exploreProducts: 'Explore products'
  },
  zh: {
    home: 'AgentecFusion 首页', primary: '主导航', mobile: '移动端导航', language: '选择语言', contact: '联系我们', skip: '跳到正文',
    openMenu: '打开菜单', closeMenu: '关闭菜单',
    nav: [['/products', '产品'], ['/solutions', '定制服务'], ['/partnerships', '合作'], ['/about', '关于我们']],
    tagline: '面向日常工作的 AI 产品。', tel: '电话', email: '邮箱',
    products: '产品', company: '公司', connect: '关注我们', account: '账户中心', admin: '管理后台',
    privacy: '隐私政策', terms: '服务条款', rights: '保留所有权利。', motto: '研究 · 产品 · 合作',
    nextTitle: '一起构建下一步。', contactTeam: '联系团队', exploreProducts: '探索产品'
  }
};

export const PRODUCT_LINKS = [['/products/learn', 'Agentec Learn'], ['/products/nexus', 'Agentec Nexus'], ['/products/agentec-one', 'Agentec One']];

function Brand({ label }) {
  return <FadeLink className="brand" to="/" aria-label={label}><img src={logoMark} alt="" width="39" height="30" /><span>Agentec<b>Fusion</b></span></FadeLink>;
}

function LanguageToggle({ className = 'lang' }) {
  const { language, changeLanguage } = useLanguage();
  const { t } = useCopy(chrome);
  return <div className={className} role="group" aria-label={t.language}>
    <button type="button" lang="en" className={language === 'en' ? 'on' : undefined} aria-pressed={language === 'en'} onClick={() => changeLanguage('en')}>EN</button>
    <button type="button" lang="zh-CN" className={language === 'zh' ? 'on' : undefined} aria-pressed={language === 'zh'} onClick={() => changeLanguage('zh')}>中文</button>
  </div>;
}

export function Header() {
  const { t } = useCopy(chrome);
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const header = useRef(null);
  const toggle = useRef(null);
  const onContact = pathname === '/contact';
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') { setOpen(false); toggle.current?.focus(); } };
    const onPointer = (event) => { if (!header.current?.contains(event.target)) setOpen(false); };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onPointer); };
  }, [open]);
  const isCurrent = (path) => pathname === path || pathname.startsWith(path + '/');
  return <>
    <a className="skip-link" href="#main-content" onClick={(event) => { event.preventDefault(); const main = document.getElementById('main-content'); main?.focus(); main?.scrollIntoView(); }}>{t.skip}</a>
    <header ref={header} className={cx('nav', open && 'open')}>
      <div className="wrap">
        <Brand label={t.home} />
        <nav className="menu" aria-label={t.primary}>
          {t.nav.map(([path, label]) => <FadeLink key={path} to={path} className={isCurrent(path) ? 'cur' : undefined} aria-current={isCurrent(path) ? 'page' : undefined}>{label}</FadeLink>)}
        </nav>
        <div className="nav-right">
          <LanguageToggle />
          <FadeLink className={onContact ? 'btn btn-light cur' : 'btn btn-dark'} to="/contact" aria-current={onContact ? 'page' : undefined}>{t.contact} <span className="ar" aria-hidden="true">↗</span></FadeLink>
          <button ref={toggle} type="button" className="burger" aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? t.closeMenu : t.openMenu} onClick={() => setOpen(!open)}>
            <span aria-hidden="true" /><span aria-hidden="true" />
          </button>
        </div>
      </div>
      <div id="mobile-menu" className="mnav" hidden={!open}>
        <nav aria-label={t.mobile}>
          {t.nav.map(([path, label]) => <FadeLink key={path} to={path} className={isCurrent(path) ? 'cur' : undefined} aria-current={isCurrent(path) ? 'page' : undefined}>{label}<span aria-hidden="true">→</span></FadeLink>)}
          <FadeLink to="/contact" className={onContact ? 'cur' : undefined}>{t.contact}<span aria-hidden="true">↗</span></FadeLink>
        </nav>
        <LanguageToggle className="lang lang-m" />
      </div>
    </header>
  </>;
}

export function Footer() {
  const { t } = useCopy(chrome);
  const { pathname } = useLocation();
  return <footer>
    <div className="wrap">
      <div className="fcols">
        <div className="fbrand"><Brand label={t.home} />
          <p>{t.tagline}</p>
          <dl><dt>{t.tel}</dt><dd><a href={CONTACT.phoneHref}>{CONTACT.phone}</a></dd><dt>{t.email}</dt><dd><a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></dd></dl></div>
        <div className="flinks">
          <div><h4>{t.products}</h4>{PRODUCT_LINKS.map(([path, name]) => <FadeLink key={path} to={path}>{name}</FadeLink>)}<a href="/one/">{t.account}</a></div>
          <div><h4>{t.company}</h4>{t.nav.slice(1).map(([path, label]) => <FadeLink key={path} to={path}>{label}</FadeLink>)}<FadeLink to="/contact">{t.contact}</FadeLink><a href="/one/admin">{t.admin}</a></div>
          <div><h4>{t.connect}</h4><ExternalLink href={CONTACT.linkedin}>LinkedIn ↗</ExternalLink><FadeLink to="/privacy">{t.privacy}</FadeLink><FadeLink to="/terms">{t.terms}</FadeLink></div>
        </div>
      </div>
      <div className="fbase"><span>© {new Date().getFullYear()} AgentecFusion. {t.rights}</span><span>{t.motto}</span></div>
    </div>
    {pathname === '/contact' ? <div style={{ height: 24 }} /> : <div className="wm" aria-hidden="true">AgentecFusion</div>}
  </footer>;
}

// "Let's build what's next." closing band on secondary pages.
export function NextCta({ secondary }) {
  const { t } = useCopy(chrome);
  return <Section className="next">
    <div className="wrap" data-reveal="">
      <h2>{t.nextTitle}</h2>
      <div className="btns">
        <FadeLink className="btn btn-dark" to="/contact">{t.contactTeam} <span className="ar" aria-hidden="true">↗</span></FadeLink>
        {secondary || <FadeLink className="btn btn-ghost" to="/products">{t.exploreProducts} <span className="ar" aria-hidden="true">↗</span></FadeLink>}
      </div>
    </div>
  </Section>;
}

const TITLES = {
  '/': ['AI Products & Solutions', 'AI 产品与定制服务'],
  '/products': ['Products', '产品'], '/solutions': ['Solutions', '定制服务'],
  '/partnerships': ['Partnerships', '合作'], '/about': ['About', '关于我们'],
  '/contact': ['Contact', '联系我们'], '/privacy': ['Privacy', '隐私政策'], '/terms': ['Terms', '服务条款'], '/safeclaw': ['SafeClaw', 'SafeClaw']
};
const PRODUCT_TITLES = { learn: 'Agentec Learn', nexus: 'Agentec Nexus', 'agentec-one': 'Agentec One' };

export function pageKey(pathname) {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/products/')) return 'learn';
  const key = pathname.split('/')[1];
  return ['products', 'solutions', 'partnerships', 'about', 'contact'].includes(key) ? key : 'misc';
}

export function RouteEffects() {
  const { pathname, hash } = useLocation();
  const { language } = useLanguage();
  useEffect(() => {
    document.documentElement.classList.remove('leaving');
    const body = document.body;
    body.style.animation = 'none';
    void body.offsetWidth;
    body.style.animation = '';
  }, [pathname]);
  useEffect(() => {
    if (hash) {
      let id = hash.slice(1);
      try { id = decodeURIComponent(id); } catch { /* An invalid fragment simply matches nothing. */ }
      const target = document.getElementById(id);
      if (target) { target.scrollIntoView(); return; }
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.getElementById('main-content')?.focus({ preventScroll: true });
  }, [pathname, hash]);
  useEffect(() => {
    const zh = language === 'zh';
    const product = PRODUCT_TITLES[pathname.split('/')[2]];
    const page = product || (TITLES[pathname] || ['Page not found', '页面未找到'])[zh ? 1 : 0];
    document.title = `${page} | AgentecFusion`;
  }, [pathname, language]);
  return null;
}
